/**
 * Storefront v7 — the tenant theme engine (the "cinematic" preset).
 *
 * ONE derivation from the tenant's brand record to every colour, face and
 * radius the storefront uses. Nothing is typed per tenant:
 *
 *   paper   the page: the merchant's page_bg_color, else warm cream.
 *   primary the ONE accent (emphasis line, progress, small accents): the
 *           merchant's primary_color clamped until it reads ≥ 3:1 on the paper.
 *   ink     headlines, body, dark slabs and the footer: the merchant's
 *           ink_color, else a DEEP tone of their primary hue (a green kitchen
 *           gets a forest ink, a terracotta one a deep brown), else forest.
 *   cta     filled buttons: the primary when text reads on it at 4.5:1,
 *           otherwise the ink — never a button nobody can read.
 *   primaryText the accent as SMALL text (links, icons): darkened until 4.5:1.
 *
 * The output is a flat token object and a CSS-variable map the page shell
 * (and every portalled dialog) sets on its own node. Reuses the colour maths
 * that already lives in `storefrontTheme.js` so the v5.66 storefront and the
 * v7 system can never disagree on a luminance.
 */
import {
  mix, darken, lighten, luminance, isDark, contrastRatio, bestTextOn,
  HEADING_FONTS, BODY_FONTS, SCRIPT_FONTS, fontStack,
} from '../../mealpass/storefrontTheme';

// Re-exported so a contract can measure the property this module PROMISES
// (>= 3:1 on the paper) rather than the weaker "the value moved".
export { contrastRatio };

// ── Locked preset (the brief: cream, forest, orange) ─────────────────────
export const V6_DEFAULT_PRIMARY = '#ef742b';   // orange — only when a tenant has no primary
export const V6_PAPER = '#fffaf0';             // cream
export const V6_INK = '#183e32';               // forest
export const V6_DEFAULT_HEADING_FONT = 'Inter Tight';
export const V6_DEFAULT_BODY_FONT = 'Inter';
export const V6_DEFAULT_SCRIPT_FONT = 'Caveat';

export const V6_RADIUS = {
  soft: { card: '20px', input: '14px', photo: '18px', pill: '999px', stage: '28px' },
  round: { card: '28px', input: '18px', photo: '24px', pill: '999px', stage: '36px' },
  sharp: { card: '6px', input: '6px', photo: '6px', pill: '8px', stage: '10px' },
};

const HEX = /^#[0-9a-fA-F]{6}$/;
const safeHex = (v, fallback) => (typeof v === 'string' && HEX.test(v.trim()) ? v.trim().toLowerCase() : fallback);

/**
 * A brand colour must read as an EMPHASIS line on the paper. Below 3:1
 * against the paper (a pastel, a yellow) it is darkened step by step until
 * it clears — the merchant keeps their hue, the customer keeps legibility.
 */
export const clampAccent = (hex, paper) => {
  // the direction follows the PAPER, exactly as clampText does: darkening a dark
  // brand colour on a dark page only buries it further, which is why this used to
  // be skipped there - and skipping it broke the >= 3:1 promise above.
  const step = isDark(paper) ? (c) => lighten(c, 0.12) : (c) => darken(c, 0.12);
  let out = hex;
  let guard = 0;
  while (contrastRatio(out, paper) < 3 && guard < 12) {
    out = step(out);
    guard += 1;
  }
  return out;
};

/** The accent as SMALL text (links, icons, list marks): pushed away from the
 *  paper (darkened on a light page, lightened on a dark one) until 4.5:1. */
export const clampText = (hex, paper) => {
  const step = isDark(paper) ? (c) => lighten(c, 0.1) : (c) => darken(c, 0.1);
  let out = hex;
  let guard = 0;
  while (contrastRatio(out, paper) < 4.5 && guard < 14) {
    out = step(out);
    guard += 1;
  }
  return out;
};

/**
 * The ink is a DEEP tone of the brand hue: the primary pulled toward a
 * near-black, then darkened until it is genuinely dark (luminance ≤ 0.035),
 * so headlines and the footer carry the brand instead of a generic black.
 */
export const deriveInk = (primary) => {
  let out = mix(primary, '#17161a', 0.3);
  let guard = 0;
  while (luminance(out) > 0.035 && guard < 12) {
    out = darken(out, 0.14);
    guard += 1;
  }
  return out;
};

/** Kept for callers of the v6 name: the dark surface is the ink pulled toward the primary. */
export const deriveDark = (primary, ink) => {
  let out = mix(ink, primary, 0.22);
  let guard = 0;
  while (luminance(out) > 0.08 && guard < 6) {
    out = darken(out, 0.15);
    guard += 1;
  }
  return out;
};

const pickFont = (value, catalogue, fallback) =>
  (catalogue.some((f) => f.family === value) ? value : fallback);

/**
 * deriveTheme(brand) → tokens.
 * `brand` is the merged storefront config (tenant row + store override) the
 * public branding endpoint returns; every key is optional.
 */
export const deriveTheme = (brand = {}) => {
  const b = brand || {};
  const paper = safeHex(b.page_bg_color, V6_PAPER);
  const paperIsDark = isDark(paper);
  const merchantPrimary = safeHex(b.primary_color, null);
  const rawPrimary = merchantPrimary || V6_DEFAULT_PRIMARY;
  const primary = clampAccent(rawPrimary, paper);
  // the ink follows the merchant's hue; the preset pairs forest with orange
  const ink = safeHex(b.ink_color, paperIsDark ? '#f5f1ea' : (merchantPrimary ? deriveInk(primary) : V6_INK));
  const accent = safeHex(b.accent_color, paperIsDark ? lighten(primary, 0.35) : lighten(primary, 0.55));
  const dark = safeHex(b.dark_surface_color, paperIsDark ? darken(paper, 0.2) : ink);

  const paperAlt = paperIsDark ? lighten(paper, 0.06) : darken(paper, 0.035);
  const line = paperIsDark ? lighten(paper, 0.16) : mix(paper, ink, 0.12);
  const inkMuted = paperIsDark ? lighten(paper, 0.55) : mix(ink, paper, 0.42);

  // filled controls: the primary only when LIGHT text reads on it (a brand-red
  // button with white type), otherwise the ink (the preset's forest button)
  const onPrimary = bestTextOn(primary, ['#ffffff', ink, '#17161a', paper]);
  const lightOnPrimary = bestTextOn(primary, ['#ffffff', paper]);
  const cta = !paperIsDark && contrastRatio(lightOnPrimary, primary) < 4.5 ? ink : primary;
  const onCta = bestTextOn(cta, ['#ffffff', paper, ink, '#17161a']);
  const ctaHover = isDark(cta) ? lighten(cta, 0.08) : darken(cta, 0.08);

  const headingFont = pickFont(b.heading_font, HEADING_FONTS, V6_DEFAULT_HEADING_FONT);
  const bodyFont = pickFont(b.body_font, BODY_FONTS, V6_DEFAULT_BODY_FONT);
  const scriptFont = pickFont(b.accent_font, SCRIPT_FONTS, V6_DEFAULT_SCRIPT_FONT);
  const radius = V6_RADIUS[b.corner_style] || V6_RADIUS.soft;

  return {
    // surfaces
    paper, paperAlt, ink, inkMuted, line, dark,
    tintInk: paperIsDark ? lighten(paper, 0.04) : mix(paper, ink, 0.08),
    tintPrimary: paperIsDark ? mix(paper, primary, 0.12) : mix(paper, primary, 0.1),
    // the one accent + its states
    primary,
    primaryHover: darken(primary, 0.08),
    primaryActive: darken(primary, 0.16),
    primarySoft: mix(paper, primary, 0.1),
    primaryTint: mix(paper, primary, 0.05),
    primaryText: clampText(primary, paper),
    accent,
    // filled controls
    cta, ctaHover, onCta,
    // Announcements use the merchant's actual brand colour with contrasting text.
    announcement: merchantPrimary || cta,
    onAnnouncement: bestTextOn(merchantPrimary || cta, ['#ffffff', ink, '#17161a', paper]),
    // text on surfaces, by contrast
    onPrimary,
    onDark: bestTextOn(dark, [V6_PAPER, paper, '#ffffff', ink]),
    onPhoto: '#ffffff',
    // status
    open: '#1F7A4D',
    closed: '#B42318',
    warn: '#9A6700',
    // type
    headingFont, bodyFont, scriptFont,
    headingStack: fontStack(headingFont, V6_DEFAULT_HEADING_FONT),
    bodyStack: fontStack(bodyFont, V6_DEFAULT_BODY_FONT),
    scriptStack: fontStack(scriptFont, V6_DEFAULT_SCRIPT_FONT),
    accentStyle: b.accent_style === 'script' ? 'script' : 'highlight',
    // shape
    radiusCard: radius.card,
    radiusInput: radius.input,
    radiusPhoto: radius.photo,
    radiusPill: radius.pill,
    radiusStage: radius.stage,
  };
};

/** The CSS custom properties the shell exposes. */
export const themeToCssVars = (t) => ({
  '--s6-paper': t.paper,
  '--s6-paper-alt': t.paperAlt,
  '--s6-ink': t.ink,
  '--s6-ink-muted': t.inkMuted,
  '--s6-line': t.line,
  '--s6-dark': t.dark,
  '--s6-tint-ink': t.tintInk,
  '--s6-tint-primary': t.tintPrimary,
  '--s6-primary': t.primary,
  '--s6-primary-hover': t.primaryHover,
  '--s6-primary-active': t.primaryActive,
  '--s6-primary-soft': t.primarySoft,
  '--s6-primary-tint': t.primaryTint,
  '--s6-primary-text': t.primaryText,
  '--s6-accent': t.accent,
  '--s6-cta': t.cta,
  '--s6-cta-hover': t.ctaHover,
  '--s6-on-cta': t.onCta,
  '--s6-announcement': t.announcement,
  '--s6-on-announcement': t.onAnnouncement,
  '--s6-on-primary': t.onPrimary,
  '--s6-on-dark': t.onDark,
  '--s6-open': t.open,
  '--s6-closed': t.closed,
  '--s6-warn': t.warn,
  '--s6-heading': t.headingStack,
  '--s6-body': t.bodyStack,
  '--s6-script': t.scriptStack,
  '--s6-radius-card': t.radiusCard,
  '--s6-radius-input': t.radiusInput,
  '--s6-radius-photo': t.radiusPhoto,
  '--s6-radius-pill': t.radiusPill,
  '--s6-radius-stage': t.radiusStage,
});

export default deriveTheme;
